import React, { useEffect, useMemo, useState } from 'react'
import { get, post } from 'services/network'
import { toast } from 'react-toastify'
import CustomToast from 'components/common/custom-toast/CustomToast'
import TableWrapper from 'components/common/table-wrapper/TableWrapper'
import { alertTypes } from 'constants/Common'
import { getAccessToken } from 'utils/common'
import AddIcon from 'assets/images/shell/icon-add.svg'
import MoreIcon from 'assets/images/shell/icon-more.svg'
import AddOrganizationModal from './AddOrganizationModal'
import './Organizations.scss'

function Organizations() {
  const [organizations, setOrganizations] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [openActionId, setOpenActionId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [isModalVisible, setModalVisible] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState(null)

  const fetchOrganizations = async () => {
    setLoading(true)
    try {
      const response = await get('organization', false, null, null, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      const payload = Array.isArray(response)
        ? response
        : response?.organizations || response?.data || []
      setOrganizations(payload)
    } catch (error) {
      setOrganizations([])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchOrganizations()
  }, [])

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  })

  const filteredOrganizations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return organizations

    return organizations.filter((item) => {
      const name = String(
        item.org_name || item.name || item.organizationName || ''
      ).toLowerCase()
      const id = String(item.organizationId || item.id || '').toLowerCase()
      return name.includes(query) || id.includes(query)
    })
  }, [organizations, searchQuery])
  const totalPages = Math.max(1, Math.ceil(filteredOrganizations.length / perPage))
  const currentPage = Math.min(page, totalPages)
  const visibleOrganizations = filteredOrganizations.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  )

  const handleOpenAddModal = () => {
    setSelectedOrganization(null)
    setModalVisible(true)
  }

  const handleOpenEditModal = (organization) => {
    setSelectedOrganization(organization)
    setModalVisible(true)
  }

  const handleCloseModal = () => {
    setModalVisible(false)
    setSelectedOrganization(null)
  }

  const handleSubmitOrganization = async (values, isEditMode) => {
    setSaving(true)
    try {
      if (isEditMode) {
        await post(
          `organization/update/${values.organizationId}`,
          {
            primary: values.primary,
            gradientFirstColor: values.gradientFirstColor,
            eventCategoryAccent: values.eventCategoryAccent,
            logo: values.logo,
          },
          false,
          null,
          getAuthHeaders()
        )
        toast(
          <CustomToast
            variant={alertTypes.SUCCESS}
            message='Organization updated successfully'
          />
        )
      } else {
        await post(
          'organization/create',
          {
            name: values.name,
            primary: values.primary,
            gradientFirstColor: values.gradientFirstColor,
            eventCategoryAccent: values.eventCategoryAccent,
            logo: values.logo,
          },
          false,
          null,
          getAuthHeaders()
        )
        toast(
          <CustomToast
            variant={alertTypes.SUCCESS}
            message='Organization created successfully'
          />
        )
      }

      handleCloseModal()
      await fetchOrganizations()
      return true
    } catch (error) {
      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message={
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            isEditMode
              ? 'Error updating organization'
              : 'Error creating organization'
          }
        />
      )
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleToggleOrganization = async (organizationId, enabled) => {
    if (!organizationId) return
    setSaving(true)
    try {
      await post(
        `organization/status/${organizationId}`,
        { enabled },
        false,
        null,
        getAuthHeaders()
      )
      setOrganizations((current) =>
        current.map((item) =>
          (item.organizationId || item.id) === organizationId
            ? { ...item, enabled }
            : item
        )
      )
    } catch (error) {
      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message={
            error?.response?.data?.error ||
            'Error updating organization status'
          }
        />
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='organizations-main'>
      <div className='container-fluid'>
        <TableWrapper
          eventFilterHeader
          footerVariant='listing'
          totalLabel='Total Organizations'
          searchPlaceholder='Search...'
          searchValue={searchQuery}
          setSearchQuery={(value) => {
            setSearchQuery(value)
            setPage(1)
          }}
          totalListCount={filteredOrganizations.length}
          pageSize={perPage}
          currentPage={currentPage}
          onPageChange={setPage}
          setPerPage={(option) => {
            setPerPage(option.value)
            setPage(1)
          }}
          actionButtons={[
            {
              label: 'Add organization',
              ariaLabel: 'Add organization',
              icon: <img src={AddIcon} width={20} height={20} alt='' />,
              handleClick: handleOpenAddModal,
            },
          ]}
        >
          {loading ? (
            <p style={{ textAlign: 'center' }}>Loading</p>
          ) : (
            <table className='table organization-table'>
              <thead>
                <tr>
                  <th scope='col'>Organization Name</th>
                  <th scope='col'>Organization Id</th>
                  <th scope='col' className='organization-table__status'>
                    Status
                  </th>
                  <th scope='col' className='organization-table__actions'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleOrganizations.map((item) => {
                  const itemId = item.organizationId || item.id
                  const name =
                    item.org_name || item.name || item.organizationName || 'N/A'
                  const isEnabled = item.enabled !== false
                  return (
                    <tr key={String(itemId)}>
                      <td>
                        <p className='organization-cell' title={name}>
                          {name}
                        </p>
                      </td>
                      <td>
                        <p className='organization-cell' title={itemId}>
                          {itemId || 'N/A'}
                        </p>
                      </td>
                      <td className='organization-table__status'>
                        <label className='organization-switch'>
                          <span className='organization-switch__control'>
                            <input
                              type='checkbox'
                              role='switch'
                              aria-label={
                                isEnabled
                                  ? `Disable ${name}`
                                  : `Enable ${name}`
                              }
                              checked={isEnabled}
                              disabled={saving}
                              onChange={(event) =>
                                handleToggleOrganization(
                                  itemId,
                                  event.target.checked
                                )
                              }
                            />
                            <span />
                          </span>
                        </label>
                      </td>
                      <td className='organization-table__actions'>
                        <div className='organization-actions'>
                          <button
                            type='button'
                            className='organization-actions__button'
                            aria-label='Organization actions'
                            disabled={saving}
                            onClick={() =>
                              setOpenActionId((current) =>
                                current === itemId ? null : itemId
                              )
                            }
                          >
                            <img src={MoreIcon} width={20} height={20} alt='' />
                          </button>
                          {openActionId === itemId && (
                            <div className='organization-actions__menu'>
                              <button
                                type='button'
                                onClick={() => {
                                  setOpenActionId(null)
                                  handleOpenEditModal(item)
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </TableWrapper>
      </div>

      <AddOrganizationModal
        show={isModalVisible}
        saving={saving}
        organization={selectedOrganization}
        onHide={handleCloseModal}
        onSubmit={handleSubmitOrganization}
      />
    </div>
  )
}

export default Organizations
